import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "How revenue works",
  "description": "Learn what governs natural resource extraction in the U.S., how resources result in federal revenue, and what processes ensure accuracy and accountability in this industry.",
  "tags": ["How revenue works", "Coal", "Oil", "Gas", "Renewables", "Laws", "Regulations", "Audit"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Box = makeShortcode("Box");
const Container = makeShortcode("Container");
const Grid = makeShortcode("Grid");
const HowItWorksRibbonGraphicImg = makeShortcode("HowItWorksRibbonGraphicImg");
const PageToc = makeShortcode("PageToc");
const ProcessCard = makeShortcode("ProcessCard");
const Tabtastic = makeShortcode("Tabtastic");
const TabtasticTab = makeShortcode("TabtasticTab");
const GlossaryTerm = makeShortcode("GlossaryTerm");
const Hidden = makeShortcode("Hidden");
const Arrow = makeShortcode("Arrow");
const Link = makeShortcode("Link");
const HowMainIconOilImg = makeShortcode("HowMainIconOilImg");
const HowMainIconCoalImg = makeShortcode("HowMainIconCoalImg");
const HowMainIconHardrockImg = makeShortcode("HowMainIconHardrockImg");
const HowMainIconWindImg = makeShortcode("HowMainIconWindImg");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <Box bgcolor='primary.main' mdxType="Box">
      <Container maxWidth="lg" mdxType="Container">
        <Grid container spacing={2} alignItems='center' mdxType="Grid">
          <Grid item xs={12} sm={8} mdxType="Grid">
            <Box p={3} pl={0} mdxType="Box">
              <h1 {...{
                "id": "How-Revenue-Works",
                "style": {
                  "position": "relative"
                }
              }}>{`How Revenue Works`}</h1>
              <p>{`Private individuals and corporations as well as the federal, state and local government can own the land and the resources beneath it. This makes the U.S. different from nearly every other country. In many places, oil, gas, coal, and other minerals belong to the government. But, in the U.S. there is widespread private ownership of these resources.`}</p>
              <br />
Learn about the process of natural resource extraction in the U.S. from land ownership to disbursement of revenue and what processes ensure accuracy.
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} mdxType="Grid">
            <Box p={3} border={4} borderTop={0} borderBottom={0} borderColor='white' mdxType="Box">
              <p><strong parentName="p">{`Natural resource extraction in the U.S. is governed by laws and regulations tied primarily to land ownership and the commodities being extracted.`}</strong></p>
              <Box align='center' mdxType="Box">
  <HowItWorksRibbonGraphicImg style={{
                  height: '100px',
                  maxWidth: '100%',
                  alignSelf: 'center'
                }} mdxType="HowItWorksRibbonGraphicImg"></HowItWorksRibbonGraphicImg>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
    {
      /* Begin content */
    }
    <Container maxWidth="lg" component="section" mdxType="Container">
      <Grid container mdxType="Grid">
        <Grid item xs={12} sm={3} mdxType="Grid">
          <PageToc scrollOffset={190} excludeClassNames={['not_toc']} mdxType="PageToc"></PageToc>
        </Grid>
        <Grid item xs={12} sm={9} mdxType="Grid">
          <h2 alt='Our data'>How our data fits together</h2>
          <hr></hr>
          <Grid container direction="row" justify="center" alignItems="stretch" spacing={0} mdxType="Grid">
  <Grid item xs={12} sm={3} mdxType="Grid">
    <ProcessCard name={'Ownership'} padding={0} contentIndent={0} centerTitle={true} mdxType="ProcessCard">
      <Tabtastic selectedTab={props.location.search} enableDataFilterContext={false} processCardTabStyle={true} mdxType="Tabtastic">
        <TabtasticTab label="Description" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            The data on this website is for energy and minerals on federal lands, <GlossaryTerm mdxType="GlossaryTerm">Native American lands</GlossaryTerm>, and the <GlossaryTerm mdxType="GlossaryTerm">Outer Continental Shelf</GlossaryTerm>. Private individuals, corporations, and state and local governments can also buy and own land. Whomever owns the land also owns the oil, gas, coal, and other minerals found below the surface.
          </Box>
        </TabtasticTab>
        <TabtasticTab label="Types" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            Types of land ownership:
            <ul>
              <li><GlossaryTerm mdxType="GlossaryTerm">Private</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Federal</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">State & local</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Native American</GlossaryTerm></li>
            </ul>
          </Box>
        </TabtasticTab>
      </Tabtastic>
    </ProcessCard>
  </Grid>
  <Grid item xs={12} sm={1} mdxType="Grid">
    <Hidden xsDown mdxType="Hidden">
      <Arrow length={80} mdxType="Arrow" />
    </Hidden>
  </Grid>
  <Grid item xs={12} sm={3} mdxType="Grid">
    <ProcessCard name={'Leasing'} padding={0} contentIndent={0} centerTitle={true} mdxType="ProcessCard">
      <Tabtastic selectedTab={props.location.search} enableDataFilterContext={false} processCardTabStyle={true} mdxType="Tabtastic">
        <TabtasticTab label="Description" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            The Bureau of Ocean Energy Management (BOEM) manages offshore leasing and the Bureau of Land Management (BLM) manages onshore leasing. BOEM and BLM award leases through a competitive bidding process. Also, the Bureau of Indian Affairs (BIA), in consultation with BLM, manages leases on Native American lands.
          </Box>
        </TabtasticTab>
        <TabtasticTab label="Types" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            Types of leases:<br />
            BOEM (federal offshore)
            <ul>
              <li>Oil & gas</li>
              <li>Wind</li>
            </ul>
            BLM (Federal Onshore)
            <ul>
              <li>Coal</li>
              <li>Oil & gas</li>
              <li>Non-energy minerals</li>
              <li>Renewable energy</li>
            </ul>
            BIA (Native American onshore)
            <ul>
              <li>Coal</li>
              <li>Oil & gas</li>
              <li>Non-energy minerals</li>
            </ul>
          </Box>
        </TabtasticTab>
      </Tabtastic>
    </ProcessCard>
  </Grid>
  <Grid item xs={12} sm={1} mdxType="Grid">
    <Hidden xsDown mdxType="Hidden">
      <Arrow length={80} mdxType="Arrow" />
    </Hidden>
  </Grid>
  <Grid item xs={12} sm={4} mdxType="Grid">
    <ProcessCard name={'Production'} padding={0} contentIndent={0} centerTitle={true} mdxType="ProcessCard">
      <Tabtastic selectedTab={props.location.search} enableDataFilterContext={false} processCardTabStyle={true} mdxType="Tabtastic">
        <TabtasticTab label="Description" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            Once a piece of leased land starts producing a commodity, like oil, the producer generally sells it. Though, the producer may also use it on the lease or disposes of it. The proceeds of selling the commodity are subject to royalties, which result in revenue to the government.
            <br />
            <Link href="/query-data/?dataType=Production" linkType="FilterTable" mdxType="Link">Query production data</Link>
          </Box>
        </TabtasticTab>
        <TabtasticTab label="Types" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            Types of commodities:
            <ul>
              <li>Oil & gas</li>
              <li>Coal</li>
              <li>Renewable energy</li>
              <li>Non-energy minerals</li>
            </ul>
          </Box>
        </TabtasticTab>
        <TabtasticTab label="Data usage" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            Production data can be used to:
            <ul>
              <li>Analyze production volumes for coal, gas, and oil.</li>
              <li>Analyze volumes by state, county, and offshore region.</li>
              <li>Analyze national Native American production volumes.</li>
              <li>Calculate total volumes for all commodities extracted on federal or Native American land.</li>
              <li>Find data by FIPS code.</li>
            </ul>
          </Box>
        </TabtasticTab>
      </Tabtastic>
    </ProcessCard>
  </Grid>
  <Grid item xs={12} sm={4} mdxType="Grid">
  </Grid>
  <Grid item xs={12} sm={3} mdxType="Grid">
    <Hidden xsDown mdxType="Hidden">
      <Arrow length={80} vertical={true} mdxType="Arrow" />
    </Hidden>
  </Grid>
  <Grid item xs={12} sm={1} mdxType="Grid">
  </Grid>
  <Grid item xs={12} sm={4} mdxType="Grid">
    <Hidden xsDown mdxType="Hidden">
      <Arrow length={80} vertical={true} mdxType="Arrow" />
    </Hidden>
  </Grid>
  <Grid item xs={12} sm={5} mdxType="Grid">
  </Grid>
  <Grid item xs={12} sm={6} mdxType="Grid">
    <ProcessCard name={'Revenue'} padding={0} contentIndent={0} centerTitle={true} mdxType="ProcessCard">
      <Tabtastic selectedTab={props.location.search} enableDataFilterContext={false} processCardTabStyle={true} mdxType="Tabtastic">
        <TabtasticTab label="Description" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            ONRR collects revenues for leases, sales, and production and then disburses them. ONRR reports revenue data in accounting year, which is every sale that is accounted for in a given period regardless of when the sale took place. Company revenue includes revenues by production phase and commodity by company. This dataset includes revenues for U.S. federal lands and offshore areas.
            <br />
            <Link href='/query-data/?dataType=Revenue' linkType='FilterTable' mdxType="Link">Query revenue data</Link>
            <Link href='/query-data/?dataType=Federal revenue by company' linkType='FilterTable' mdxType="Link">Query company revenue data</Link>
          </Box>
        </TabtasticTab>
        <TabtasticTab label="Types" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            Other Types of Revenue:
            <ul>
              <li><GlossaryTerm mdxType="GlossaryTerm">Royalties</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Bonus</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Rent</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Inspection fees</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Civil penalties</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Other revenues</GlossaryTerm></li>
            </ul>
          </Box>
        </TabtasticTab>
        <TabtasticTab label="Data usage" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            Revenue data can be used to:
            <ul>
              <li>Analyze revenue trends for money collected by the federal government.</li>
              <li>Analyze revenue by type.</li>
              <li>Calculate amount of money collected by federal government from energy and mineral extractions.</li>
              <li>Calculate revenues by commodity.</li>
              <li>Analyze revenue by state, county, or offshore region.</li>
              <li>Analyze national Native American revenues.</li>
              <li>Analyze monthly revenue by accounting year.</li>
              <li>Analyze revenue by location for Federal onshore and Federal Offshore.</li>
            </ul>
            Company revenue data can be used to:
            <ul>
              <li>Calculate amount of money paid by fiscal year to the federal government from energy and mineral extracting companies.</li>
              <li>Analyze revenue type (bonuses, rents, royalties, etc.) by company.</li>
            </ul>
          </Box>
        </TabtasticTab>
      </Tabtastic>
    </ProcessCard>
  </Grid>
  <Grid item xs={12} sm={1} mdxType="Grid">
  </Grid>
  <Grid item xs={12} sm={5} mdxType="Grid">
  </Grid>
  <Grid item xs={12} sm={6} mdxType="Grid">
    <Hidden xsDown mdxType="Hidden">
      <Arrow length={80} vertical={true} mdxType="Arrow" />
    </Hidden>
  </Grid>
  <Grid item xs={12} sm={1} mdxType="Grid">
  </Grid>
  <Grid item xs={12} sm={5} mdxType="Grid">
  </Grid>
  <Grid item xs={12} sm={6} mdxType="Grid">
    <ProcessCard name={'Disbursements'} padding={0} contentIndent={0} centerTitle={true} mdxType="ProcessCard">
      <Tabtastic selectedTab={props.location.search} enableDataFilterContext={false} processCardTabStyle={true} mdxType="Tabtastic">
        <TabtasticTab label="Description" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            ONRR distributes the collected revenue to federal agencies, funds, and local governments according to federal laws and regulations.
            <br />
            <Link href='/query-data/?dataType=Disbursements' linkType='FilterTable' mdxType="Link">Query disbursements data</Link>
          </Box>
        </TabtasticTab>
        <TabtasticTab label="Types" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            Types of Disbursement:
            <ul>
              <li><GlossaryTerm mdxType="GlossaryTerm">Land and Water Conservation Fund</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Historic Preservation Fund</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">U.S. Treasury</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">State and local governments</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Reclamation Fund</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Native American tribes and individuals</GlossaryTerm></li>
              <li><GlossaryTerm mdxType="GlossaryTerm">Other funds</GlossaryTerm></li>
            </ul>
          </Box>
        </TabtasticTab>
        <TabtasticTab label="Data usage" mdxType="TabtasticTab">
          <Box fontSize={16} mdxType="Box">
            Disbursements data can be used to:
            <ul>
              <li>Calculate how much money the federal government disbursed to various funds, agencies, local governments and Native Americans.</li>
              <li>Analyze how revenue generated is being used.</li>
              <li>Calculate what states are receiving from <GlossaryTerm mdxType="GlossaryTerm">GOMESA</GlossaryTerm>.</li>
              <li>Calculate amount disbursed to the <GlossaryTerm mdxType="GlossaryTerm">Land and Water Conservation Fund</GlossaryTerm>.</li>
            </ul>
          </Box>
        </TabtasticTab>
      </Tabtastic>
    </ProcessCard>
  </Grid>
  <Grid item xs={12} sm={1} mdxType="Grid">
  </Grid>
          </Grid>
          <h2 alt='Land ownership'>Who owns natural resources in the U.S.?</h2>
          <hr></hr>
          <h3 {...{
            "id": "Ownership",
            "style": {
              "position": "relative"
            }
          }}>{`Ownership`}</h3>
          <p>{`Private individuals and corporations, as well as federal, state, local, and Native American governments, can own land and the oil, gas, coal, and other minerals found below the surface.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/ownership/"
            }}>{`Learn about land and resource ownership`}</a></p>
          <h2 alt='Laws and regulations'> What laws and regulations govern natural resource extraction in the U.S.?</h2>
          <hr></hr>
          <h3 {...{
            "id": "Federal-laws-and-regulations",
            "style": {
              "position": "relative"
            }
          }}>{`Federal laws and regulations`}</h3>
          <p>{`The legislative branch has passed many laws governing natural resource extraction on federal lands.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/federal-laws/"
            }}>{`Learn about federal laws`}</a></p>
          <h3 {...{
            "id": "Federal-reforms",
            "style": {
              "position": "relative"
            }
          }}>{`Federal reforms`}</h3>
          <p>{`The government reforms laws and regulations by enacting new legislation and proposing new rules for implementation.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/federal-reforms/"
            }}>{`Learn about reforms`}</a></p>
          <h2 alt='Natural resources on Native American land'>How are natural resources on Native American land governed?</h2>
          <hr></hr>
          <h3 {...{
            "id": "Ownership-and-governance",
            "style": {
              "position": "relative"
            }
          }}>{`Ownership and governance`}</h3>
          <p>{`Native American land ownership involves a complex patchwork of titles, restrictions, obligations, statutes, and regulations.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/native-american-ownership-governance/"
            }}>{`Learn about governance on Native American land`}</a></p>
          <h3 {...{
            "id": "Production",
            "style": {
              "position": "relative"
            }
          }}>{`Production`}</h3>
          <p>{`Extracting natural resources on Native American land involves unique processes and multiple stakeholders.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/native-american-production/"
            }}>{`Learn about production on Native American land`}</a></p>
          <h3 {...{
            "id": "Revenue",
            "style": {
              "position": "relative"
            }
          }}>{`Revenue`}</h3>
          <p>{`Natural resources are increasingly a key source of income for many Native American tribes. Several organizations within the Department of the Interior have roles and responsibilities in collecting revenue from production on Native American land.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/native-american-revenue/"
            }}>{`Learn about Native American revenues`}</a></p>
          <h2 alt='Resources to revenue'>How do natural resources result in federal revenue?</h2>
          <hr></hr>
          <h3 alt='Production'>The production process</h3>
          <Grid container alignItems='center' mdxType="Grid">
            <Grid item xs={10} mdxType="Grid">
              <h4 {...{
                "id": "Oil-and-gas",
                "style": {
                  "position": "relative"
                }
              }}>{`Oil and gas`}</h4>
              <p>{`Oil and gas (or natural gas) are fossil fuels that form underground on land and under the ocean. The U.S. is among the world's top producers of oil and gas.`}</p>
              <p><a parentName="p" {...{
                  "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/offshore-oil-gas"
                }}>{`Learn about oil and gas`}</a></p>
            </Grid>
            <Grid item xs={2} mdxType="Grid">
              <HowMainIconOilImg style={{
                opacity: '0.2'
              }} mdxType="HowMainIconOilImg"></HowMainIconOilImg>
            </Grid>
          </Grid>
          <Grid container alignItems='center' mdxType="Grid">
            <Grid item xs={10} mdxType="Grid">
              <h4 {...{
                "id": "Coal",
                "style": {
                  "position": "relative"
                }
              }}>{`Coal`}</h4>
              <p>{`Miners extract coal through surface and subsurface mining. Most of the coal produced on federal land in the U.S. is mined in the Powder River Basin of Wyoming.`}</p>
              <p><a parentName="p" {...{
                  "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/coal"
                }}>{`Learn about coal`}</a></p>
            </Grid>
            <Grid item xs={2} mdxType="Grid">
              <HowMainIconCoalImg style={{
                opacity: '0.2'
              }} mdxType="HowMainIconCoalImg"></HowMainIconCoalImg>
            </Grid>
          </Grid>
          <Grid container alignItems='center' mdxType="Grid">
            <Grid item xs={10} mdxType="Grid">
              <h4 {...{
                "id": "Nonenergy-minerals",
                "style": {
                  "position": "relative"
                }
              }}>{`Nonenergy minerals`}</h4>
              <p>{`Gold, copper, and iron are the main sources of nonenergy mineral revenues. The Mining Law of 1872 is the major federal law governing locatable minerals.`}</p>
              <p><a parentName="p" {...{
                  "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/minerals"
                }}>{`Learn about nonenergy minerals`}</a></p>
            </Grid>
            <Grid item xs={2} mdxType="Grid">
              <HowMainIconHardrockImg style={{
                opacity: '0.2'
              }} mdxType="HowMainIconHardrockImg"></HowMainIconHardrockImg>
            </Grid>
          </Grid>
          <Grid container alignItems='center' mdxType="Grid">
            <Grid item xs={10} mdxType="Grid">
              <h4 {...{
                "id": "Renewable-energy",
                "style": {
                  "position": "relative"
                }
              }}>{`Renewable energy`}</h4>
              <p>{`Renewable energy resources include geothermal, solar, wind, biomass, and hydrokinetic energy.`}</p>
              <p><a parentName="p" {...{
                  "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/onshore-renewables"
                }}>{`Learn about renewable energy`}</a></p>
            </Grid>
            <Grid item xs={2} mdxType="Grid">
              <HowMainIconWindImg style={{
                opacity: '0.2'
              }} mdxType="HowMainIconWindImg"></HowMainIconWindImg>
            </Grid>
          </Grid>
          <h3 alt='Revenues'>Understanding federal revenues</h3>
          <h4 {...{
            "id": "How-revenue-works",
            "style": {
              "position": "relative"
            }
          }}>{`How revenue works`}</h4>
          <p>{`Companies pay the federal government to extract natural resources on federal lands and waters. This revenue is collected by the Office of Natural Resources Revenue, mostly as `}<GlossaryTerm mdxType="GlossaryTerm">{`bonuses`}</GlossaryTerm>{`, `}<GlossaryTerm mdxType="GlossaryTerm">{`rent`}</GlossaryTerm>{`, and `}<GlossaryTerm mdxType="GlossaryTerm">{`royalties`}</GlossaryTerm>{`.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/revenues"
            }}>{`Learn about how revenue works`}</a></p>
          <h4 {...{
            "id": "Abandoned-Mine-Land-AML-Reclamation-Program",
            "style": {
              "position": "relative"
            }
          }}>{`Abandoned Mine Land (AML) Reclamation Program`}</h4>
          <p>{`This program uses fees from present-day coal mining companies to reclaim coal mines abandoned before 1977.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/aml-reclamation-program"
            }}>{`Learn about the AML Reclamation Program`}</a></p>
          <h4 {...{
            "id": "Coal-Excise-Tax",
            "style": {
              "position": "relative"
            }
          }}>{`Coal Excise Tax`}</h4>
          <p>{`Coal producers pay a federal excise tax, which originated in 1977 with the Black Lung Revenue Act, when they mine coal in the U.S.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/coal-excise-tax"
            }}>{`Learn about excise tax revenue`}</a></p>
          <h3 alt='Disbursements'>Understanding federal disbursements</h3>
          <h4 {...{
            "id": "How-disbursements-works",
            "style": {
              "position": "relative"
            }
          }}>{`How disbursements works`}</h4>
          <p>{`The Office of Natural Resources Revenue collects revenue from natural resources extraction on federal lands and waters and distributes that revenue according to federal law. This process is called "disbursement."`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/disbursements"
            }}>{`Learn about how disbursements work`}</a></p>
          <h4 {...{
            "id": "Land-and-Water-Conservation-Fund",
            "style": {
              "position": "relative"
            }
          }}>{`Land and Water Conservation Fund`}</h4>
          <p>{`The Land and Water Conservation Fund receives disbursements from offshore oil and gas revenue to support conservation, outdoor recreation, and the needs of local communities.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/lwcf"
            }}>{`Learn about the Land and Water Conservation Fund`}</a></p>
          {
            /*
            #### National Parks and Public Land Legacy Restoration Fund
            To address priority deferred maintenance projects, the National Parks and Public Land Legacy Restoration Fund receives disbursements from federal energy development revenues for five years starting in <GlossaryTerm>Fiscal Year (FY)</GlossaryTerm> 2021.
            [Learn about the National Parks and Public Land Legacy Restoration Fund](/how-revenue-works/nppllrf)
            */
          }
          <h4 {...{
            "id": "Historic-Preservation-Fund",
            "style": {
              "position": "relative"
            }
          }}>{`Historic Preservation Fund`}</h4>
          <p>{`When `}<GlossaryTerm mdxType="GlossaryTerm">{`authorized`}</GlossaryTerm>{`, the Historic Preservation Fund receives disbursements from offshore oil and gas revenue to fund the conservation of cultural and historic sites.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/hpf"
            }}>{`Learn about the Historic Preservation Fund`}</a></p>
          <h4 {...{
            "id": "Reclamation-Fund",
            "style": {
              "position": "relative"
            }
          }}>{`Reclamation Fund`}</h4>
          <p>{`The Reclamation Fund receives revenue from onshore natural resources extraction. The fund supports the construction, operation, and maintenance of irrigation and hydropower projects.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/reclamation"
            }}>{`Reclamation Fund`}</a></p>
          <h4 {...{
            "id": "Gulf-of-Mexico-Energy-Security-Act-GOMESA",
            "style": {
              "position": "relative"
            }
          }}>{`Gulf of Mexico Energy Security Act (GOMESA)`}</h4>
          <p>{`The Gulf of Mexico Energy Security Act (GOMESA) created a revenue-sharing model for gulf states. Four states receive a portion of the revenue from oil and gas production in the Gulf of Mexico.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/gomesa"
            }}>{`Learn about GOMESA`}</a></p>
          <h2 alt='Accountability'>How does the U.S. ensure accuracy and accountability in natural resource revenue?</h2>
          <hr></hr>
          <h3 {...{
            "id": "Audits-and-assurances",
            "style": {
              "position": "relative"
            }
          }}>{`Audits and assurances`}</h3>
          <p>{`Data about revenue from the extractive industries is subject to a number of controls, standards, and regulations.`}</p>
          <p><a parentName="p" {...{
              "href": "/sites/2375-fix-explore-data-add-location-dropdown/how-revenue-works/audits-and-assurances"
            }}>{`Learn about audits and assurances`}</a></p>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
          <br></br>
        </Grid>
      </Grid>
    </Container>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      